import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Text } from '@/design-system/atoms/Text';
import { trackInteraction } from '@/utils/analytics';

import styles from './FilterButton.module.scss';

/**
 * The FilterButton component displays a filter tag button
 * @param {object} props - The component props
 * @param {string} props.state - The state of the filter
 * @param {string} props.label - The label of the filter
 * @param {object} props.ariaLabel - The aria-label attribute
 * @param {string} props.className - Additional class name
 * @param {string} props.value - The type of filter
 * @param {number} props.numberOfResults - The count of the filter
 * @param {function} props.clickCallback - The callback function
 * @returns {React.Component} The filter tag button
 */
const FilterButton = ({
    state,
    label,
    ariaLabel,
    className,
    value,
    numberOfResults,
    clickCallback,
    analytics,
    ...props
}) => {
    const isActive = state === 'selected';

    /**
     * The click handler
     * @param {object} event - The event object
     * @returns {void}
     */
    const handleClick = React.useCallback(
        (event) => {
            event.preventDefault();
            if (clickCallback) {
                clickCallback(value);

                if (analytics && !isActive) {
                    trackInteraction({
                        actionLabel: `${value.replace(
                            /([a-z])([A-Z])/g,
                            '$1 $2',
                        )} ${numberOfResults}`,
                        ...analytics,
                    });
                }
            }
        },
        [clickCallback, value, analytics, numberOfResults, isActive],
    );

    return (
        <button
            className={classnames(
                styles['filter-button'],
                isActive && styles[`filter-button--active-${value.toLowerCase()}`],
                className,
            )}
            onClick={handleClick}
            aria-label={ariaLabel}
            type="button"
            disabled={numberOfResults === 0}
            data-trigger={analytics ? analytics?.selector : undefined}
            {...props}
        >
            <div className={styles['filter-button__label']}>
                <Text
                    lineClamp={1}
                    tag={Text.TAG.SPAN}
                    variant={Text.VARIANT.E1}
                    content={label.replace(/([a-z])([A-Z])/g, '$1 $2')}
                />
            </div>
            <div className={styles['filter-button__count']}>
                <Text tag={Text.TAG.SPAN} variant={Text.VARIANT.E1} content={numberOfResults} />
            </div>
        </button>
    );
};

FilterButton.displayName = 'Filter';

FilterButton.propTypes = {
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    numberOfResults: PropTypes.number,
    state: PropTypes.string,
    value: PropTypes.string,
};

FilterButton.defaultProps = {
    numberOfResults: 0,
    state: 'idle',
    value: '',
};

export default FilterButton;
